import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import debounce from 'lodash/debounce';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../../../CommonComponents/OPL/Menu/Menu';
import { IconButton } from '../../../../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../../../../CommonComponents/OPL/List/ListItem';
import { WidgetContent } from '../WidgetContent/WidgetContent';
import { useWidgetsMutation } from '../../../../api/useWidgetsMutation';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { AddWidgetModal } from '../../../AddWidgetModal/AddWidgetModal';
import { ExcludeWidgetModal } from '../../../ExcludeWidgetModal/ExcludeWidgetModal';
import { WidgetDTO, WidgetsDTO } from '../../../../api/useWidgetsQuery';
import {
  ADD_CARD_BUTTON_CONTAINER_ID,
  WIDGETS_WITH_GROUP_SELECTION,
} from '../../DesktopWidgets.meta';
import { TabSlug } from '../../../../types';

interface Props {
  tab: TabSlug;
  widgets: WidgetsDTO;
}

export const EditWidgets = ({ tab, widgets: initialWidgets }: Props) => {
  const [widgets, setWidgets] = useState(initialWidgets);

  const [isAddWidgetModalOpen, setIsAddWidgetModalOpen] = useState(false);

  const [activeWidget, setActiveWidget] = useState<WidgetDTO | null>(null);

  const [widgetToExclude, setWidgetIdToExclude] = useState<WidgetDTO | null>(null);

  const { mutateAsync: mutateWidgets, isLoading: isMutatingWidgets } = useWidgetsMutation({ tab });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveWidget(widgets.included.find(({ id }) => id === active.id) || null);
  };

  const handleDragOver = ({ active, over }: DragEndEvent) => {
    setWidgets(prevWidgets => {
      return {
        ...prevWidgets,
        included: arrayMove(
          prevWidgets.included,
          prevWidgets.included.findIndex(({ id }) => active?.id === id),
          prevWidgets.included.findIndex(({ id }) => id === over?.id),
        ),
      };
    });
  };

  const debouncedHandleDragOver = useRef(debounce(handleDragOver, 300)).current;

  const handleDragEnd = () => {
    setActiveWidget(null);

    return mutateWidgets({ tab, widgets });
  };

  const toggleCardWidth = (id: string) => {
    const nextWidgets = {
      ...widgets,
      included: widgets.included.map(widget =>
        widget.id === id ? { ...widget, isFullWidth: !widget.isFullWidth } : widget,
      ),
    };

    setWidgets(nextWidgets);

    return mutateWidgets({ tab, widgets: nextWidgets });
  };

  const addCardButtonContainer = document.getElementById(ADD_CARD_BUTTON_CONTAINER_ID);

  return (
    <>
      {widgets.excluded.length > 0 &&
        addCardButtonContainer &&
        createPortal(
          <Button
            variant="subtleActive"
            leftIcon="fas fa-plus"
            onClick={() => setIsAddWidgetModalOpen(true)}
          >
            Add Card
          </Button>,
          addCardButtonContainer,
        )}

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={debouncedHandleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={widgets.included} strategy={() => null}>
          <Box display="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {widgets.included.map(widget => (
              <DraggableCard
                key={widget.id}
                toggleCardWidth={toggleCardWidth}
                isMutatingWidgets={isMutatingWidgets}
                onExcludeWidget={() => setWidgetIdToExclude(widget)}
                {...widget}
              />
            ))}
          </Box>
        </SortableContext>

        <DragOverlay>
          {activeWidget && <DraggableCard isOverlay key={activeWidget.id} {...activeWidget} />}
        </DragOverlay>
      </DndContext>

      {isAddWidgetModalOpen && (
        <AddWidgetModal
          tab={tab}
          widgets={widgets}
          onClose={() => setIsAddWidgetModalOpen(false)}
        />
      )}

      {widgetToExclude && (
        <ExcludeWidgetModal
          tab={tab}
          widgetToExclude={widgetToExclude}
          widgets={widgets}
          onClose={() => setWidgetIdToExclude(null)}
        />
      )}
    </>
  );
};

interface DraggableCardProps extends WidgetDTO {
  onExcludeWidget?(): void;
  isOverlay?: boolean;
  toggleCardWidth?(id: string): void;
  isMutatingWidgets?: boolean;
}

const DraggableCard = ({
  id,
  title,
  isOverlay,
  isFullWidth,
  isWidthAdjustable,
  toggleCardWidth,
  isMutatingWidgets,
  onExcludeWidget,
}: DraggableCardProps) => {
  const { setNodeRef, attributes, listeners, isDragging, transform, transition } = useSortable({
    id,
  });

  const readOnly = isMutatingWidgets || isOverlay || isDragging;

  return (
    <Box
      key={id}
      ref={setNodeRef}
      css={{
        gridColumn: isFullWidth ? 'span 2' : 'unset',
        opacity: isDragging ? 0.5 : 1,
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : 'none',
        transition,
      }}
    >
      {WIDGETS_WITH_GROUP_SELECTION.includes(id) && !isDragging && (
        <Box id={`group-selection--portal-target--${id}`} />
      )}

      <Box
        css={{
          position: 'relative',
          height: '100%',
          '> *': {
            height: '100%',
          },
        }}
      >
        <Box
          style={{
            display: 'flex',
            position: 'absolute',
            top: 8,
            right: 8,
            gap: 2,
          }}
        >
          <IconButton
            disabled={readOnly}
            icon="fas fa-grip-vertical"
            color="neutral.500"
            variant="subtleInactive"
            css="cursor: grab; background: none !important"
            {...attributes}
            {...listeners}
          />

          <Menu>
            <MenuTrigger disabled={readOnly}>
              <IconButton aria-label="Menu" icon="fas fa-ellipsis-h" variant="subtleInactive" />
            </MenuTrigger>

            <MenuContent>
              {isWidthAdjustable && (
                <>
                  <MenuItem
                    onSelect={() => {
                      if (toggleCardWidth) {
                        toggleCardWidth(id);
                      }
                    }}
                  >
                    <ListItem icon="fas fa-arrows-h">{`Make ${
                      isFullWidth ? 'half' : 'full'
                    } width`}</ListItem>
                  </MenuItem>

                  <MenuSeparator />
                </>
              )}

              <MenuItem onSelect={onExcludeWidget}>
                <ListItem icon="far fa-trash-alt" variant="error">
                  Delete Card
                </ListItem>
              </MenuItem>
            </MenuContent>
          </Menu>
        </Box>

        <WidgetContent id={id} title={title} />
      </Box>
    </Box>
  );
};
